import css from "./card.scss?inline";
import globalStyles from "../../index.scss?inline";
import { ArgSpecDictionary, TypedVars } from "../component-utils";
import { HHDSCardGridType } from "../CardGrid/CardGrid.enums";
import { HHDSTagGroup } from "../TagGroup/TagGroup";

const DEBUG_VERBOSE: boolean = false;
const CLASS_NAME: string = "HHDSCard";
const TAG_NAME: string = "hhds-card";
export const HHDSCardTagName: string = "hhds-card";

// ////////////////////////////////////////////////////////////////////

export const HHDSCardGridAttrNames = {
	date: "date",
	type: "type",
	morePadding: "more-padding",
	title: "title",
	description: "description",
	cta: "cta",
	url: "url",
	target: "target",
	categoryData: "category-data",
	modal: "modal",
};

const Attrs = HHDSCardGridAttrNames;

export class HHDSCard extends HTMLElement {
	private vars: TypedVars = new TypedVars(this);
	private shadow: ShadowRoot;

	constructor() {
		super();
		DEBUG_VERBOSE && console.log(CLASS_NAME, "constructed");

		this.shadow = this.attachShadow({ mode: "open" });
		if (!this.shadow) {
			return;
		}
	}

	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
	// Lifecycle Methods
	// https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_custom_elements#using_the_lifecycle_callbacks

	// Invoked each time the custom element is appended into a document-connected element.
	connectedCallback() {
		this.parseAttributes();

		this.render();

		this.addEventListener("click", this.onClick.bind(this));

		DEBUG_VERBOSE && console.log(`[${TAG_NAME}] Initialised`);
	}

	get tagGroup(): HHDSTagGroup | null {
		const slot = this.shadow.querySelector(
			'slot[name="tags"]'
		) as HTMLSlotElement;
		const tagGroup = slot.assignedElements()[0] as HHDSTagGroup;
		return tagGroup || null;
	}

	parseAttributes() {
		this.vars.parse(this, ArgSpecs);
	}

	get categoryNames(): string[] {
		const categoryData = this.vars.get<string>(Attrs.categoryData);
		const categoryDataObj = JSON.parse(categoryData);
		if (!categoryDataObj || categoryDataObj.length === 0) return [];
		return categoryDataObj.map((category: any) => category.name);
	}

	render() {
		const url = this.vars.get<string>(Attrs.url);
		const modal = this.vars.get<string>(Attrs.modal);
		const target = this.vars.get<string>(Attrs.target);
		const type = this.vars.get<string>(Attrs.type);
		const morePadding = this.vars.get<string>(Attrs.morePadding);
		const contentMorePaddingClasses = morePadding
			? "hhds-card__content--more-padding"
			: "";
		const footerMorePaddingClasses = morePadding
			? "hhds-card__footer--more-padding"
			: "";

		this.shadow.innerHTML = `
			<style>${globalStyles}</style>
			<style>${css}</style>
			<div class="hhds-card hhds-card__${type} ${modal ? `hhds-card--modal` : ``}">

				${
					url
						? `<a href="${url}" ${
								target ? `target="${target}"` : ""
						  } class="hhds-card__link"></a>`
						: ""
				}
				${
					modal
						? `<svg class="${TAG_NAME}__close" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="36" height="36" fill="black" fill-opacity="0.25"/><path d="M22.854 22.1465C22.9005 22.193 22.9373 22.2481 22.9625 22.3088C22.9876 22.3695 23.0006 22.4346 23.0006 22.5003C23.0006 22.566 22.9876 22.631 22.9625 22.6917C22.9373 22.7524 22.9005 22.8076 22.854 22.854C22.8076 22.9005 22.7524 22.9373 22.6917 22.9625C22.631 22.9876 22.566 23.0006 22.5003 23.0006C22.4346 23.0006 22.3695 22.9876 22.3088 22.9625C22.2481 22.9373 22.193 22.9005 22.1465 22.854L18.0003 18.7072L13.854 22.854C13.7602 22.9478 13.633 23.0006 13.5003 23.0006C13.3676 23.0006 13.2403 22.9478 13.1465 22.854C13.0527 22.7602 13 22.633 13 22.5003C13 22.3676 13.0527 22.2403 13.1465 22.1465L17.2934 18.0003L13.1465 13.854C13.0527 13.7602 13 13.633 13 13.5003C13 13.3676 13.0527 13.2403 13.1465 13.1465C13.2403 13.0527 13.3676 13 13.5003 13C13.633 13 13.7602 13.0527 13.854 13.1465L18.0003 17.2934L22.1465 13.1465C22.2403 13.0527 22.3676 13 22.5003 13C22.633 13 22.7602 13.0527 22.854 13.1465C22.9478 13.2403 23.0006 13.3676 23.0006 13.5003C23.0006 13.633 22.9478 13.7602 22.854 13.854L18.7072 18.0003L22.854 22.1465Z" fill="white"/></svg>`
						: ``
				}

				<div class="hhds-card__header">	
					<slot name="image"></slot>
					<slot name="tags"></slot>
				</div>

				<div class="hhds-card__content ${contentMorePaddingClasses}">
					<slot></slot>
				</div>

        <div class="hhds-card__footer ${footerMorePaddingClasses}">
					<slot name="button"></slot>	
					<slot name="logo"></slot>	
				</div>

        <div class="hhds-card__bottom">
          <slot name="home-info"></slot>
          <div class="hhds-card__multibutton_footer">
            <slot name="buttons"></slot>
          </div>
        </div>

			</div>
		`;

		const footer = this.shadow.querySelector(".hhds-card__footer");
		const buttonSlot = footer?.querySelector(
			'slot[name="button"]'
		) as HTMLSlotElement;
		const logoSlot = footer?.querySelector(
			'slot[name="logo"]'
		) as HTMLSlotElement;

		const hasButtonContent = buttonSlot?.assignedElements().length > 0;
		const hasLogoContent = logoSlot?.assignedElements().length > 0;

		if (!hasButtonContent && !hasLogoContent) footer?.remove();

		const multibuttonFooter = this.shadow.querySelector(
			".hhds-card__multibutton_footer"
		);
		const multibuttonSlot = multibuttonFooter?.querySelector(
			'slot[name="buttons"]'
		) as HTMLSlotElement;
		const multibuttonContent = multibuttonSlot?.assignedElements();
		const hasMultibuttonContent = multibuttonContent.length > 0;
		if (!hasMultibuttonContent) multibuttonFooter?.remove();

		multibuttonContent.forEach((button) => {
			button.addEventListener("keydown", (event: any) => {
				if (event.key === "Enter" || event.key === " ") {
					const href = button.getAttribute("href");
					if (href) {
						window.open(href, "_blank");
					} else {
						(button as HTMLElement).click();
					}
				}
      		});
    	});
	}

	private onClick(event: MouseEvent) {
		event.stopPropagation();
		const link = this.shadow.querySelector(
			".hhds-card__link"
		) as HTMLLinkElement;
		if (link) {
			link.click();
		}
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.date]: {
		description: "A textual publish date",
		defaultValue: "",
		type: String,
	},
	[Attrs.type]: {
		description: "The type of card.",
		defaultValue: "",
		type: HHDSCardGridType,
	},
	[Attrs.title]: {
		description: "The title text.",
		defaultValue: "",
		type: String,
	},
	[Attrs.description]: {
		description: "Additional textual information.",
		defaultValue: "",
		type: String,
	},
	[Attrs.cta]: {
		description: "An optional URL to navigate to when the item is clicked.",
		defaultValue: "",
		type: String,
	},
	[Attrs.url]: {
		description: "An optional URL to navigate to when the item is clicked.",
		defaultValue: "",
		type: String,
	},
	[Attrs.target]: {
		description:
			"Specifies where to open the URL (e.g., _blank for a new tab) if a URL is defined.",
		defaultValue: "",
		type: String,
	},
	[Attrs.categoryData]: {
		description: "The category data.",
		defaultValue: "",
		type: String,
	},
	[Attrs.morePadding]: {
		description: "Whether to add more padding to the card content and footer.",
		defaultValue: false,
		type: Boolean,
	},
	[Attrs.modal]: {
		description: "Turn card into a modal",
		defaultValue: false,
		type: Boolean,
	},
};
